import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  DISPATCHER_LIMITS,
  DISPATCHER_ERRORS,
  validateWatchCriteria,
} from "@/lib/dispatcher/limits";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";


export interface DispatcherWatch {
  id: string;
  user_id: string;
  company_id: string | null;
  label: string | null;
  current_city: string | null;
  current_state: string | null;
  current_lat: number | null;
  current_lng: number | null;
  max_deadhead_miles: number;
  equipment_types: string[];
  pickup_date_from: string | null;
  pickup_date_to: string | null;
  min_rate: number | null;
  min_rpm: number | null;
  destination_states: string[] | null;
  destination_city: string | null;
  destination_state: string | null;
  destination_radius_miles: number | null;
  max_weight_lbs: number | null;
  avoid_states: string[] | null;
  notify_in_app: boolean;
  notify_email: boolean;
  notify_sms: boolean;
  auto_score: boolean;
  status: "active" | "paused" | "archived";
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useDispatcherWatches() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [watches, setWatches] = useState<DispatcherWatch[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!userId) {
      setWatches([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("dispatcher_watches")
      .select("*")
      .eq("user_id", userId)
      .neq("status", "archived")
      .order("created_at", { ascending: false });
    setWatches((data as DispatcherWatch[]) || []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    refresh();
    if (!userId) return;
    const channel = supabase
      .channel(`dispatcher-watches-${userId.slice(-8)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "dispatcher_watches", filter: `user_id=eq.${userId}` },
        () => refresh()
      )
      .subscribe((status) => { if (status === "SUBSCRIBED") refresh(); });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refresh]);
  useRealtimeSelfHeal(refresh, !!userId);


  const createWatch = useCallback(
    async (payload: Partial<DispatcherWatch>) => {
      if (!userId) throw new Error("Not authenticated");

      // Limit 6: criteria validation
      const validation = validateWatchCriteria({
        current_city: payload.current_city,
        current_state: payload.current_state,
        destination_city: payload.destination_city,
        destination_state: payload.destination_state,
        destination_states: payload.destination_states,
        equipment_types: payload.equipment_types,
        min_rate: payload.min_rate,
        min_rpm: payload.min_rpm,
      });
      if (!validation.ok) { throw new Error((validation as { ok: false; error: string }).error); }

      // Limit 2: per-user active watch count
      const { count: activeCount, error: countErr } = await supabase
        .from("dispatcher_watches")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("status", "active");
      if (countErr) throw countErr;
      if ((activeCount ?? 0) >= DISPATCHER_LIMITS.MAX_ACTIVE_WATCHES_PER_USER) {
        throw new Error(DISPATCHER_ERRORS.WATCH_LIMIT);
      }

      const { data, error } = await supabase
        .from("dispatcher_watches")
        .insert({
          user_id: userId,
          equipment_types: payload.equipment_types || [],
          max_deadhead_miles: payload.max_deadhead_miles ?? 150,
          notify_in_app: payload.notify_in_app ?? true,
          notify_email: payload.notify_email ?? true,
          notify_sms: false,
          auto_score: payload.auto_score ?? true,
          status: "active",
          ...payload,
        })
        .select()
        .single();
      if (error) {
        const msg = (error.message || "") + " " + ((error as any).details || "");
        if (msg.includes("dispatcher_watch_limit_reached")) {
          throw new Error(DISPATCHER_ERRORS.WATCH_LIMIT);
        }
        throw error;
      }
      // Trigger immediate run
      supabase.functions.invoke("check-dispatcher-watches", { body: { watch_id: data.id } }).catch(() => {});
      await refresh();
      return data as DispatcherWatch;
    },
    [userId, refresh]
  );

  const updateWatch = useCallback(
    async (id: string, patch: Partial<DispatcherWatch>) => {
      // Re-validate criteria on update too (only the fields being changed merged with prior state would be ideal,
      // but the watch row is the source of truth; fetch current and merge)
      const { data: current } = await supabase
        .from("dispatcher_watches")
        .select("current_city,current_state,destination_city,destination_state,destination_states,equipment_types,min_rate,min_rpm")
        .eq("id", id)
        .maybeSingle();
      const merged = { ...(current || {}), ...patch };
      const validation = validateWatchCriteria(merged as any);
      if (!validation.ok) { throw new Error((validation as { ok: false; error: string }).error); }

      const { error } = await supabase.from("dispatcher_watches").update(patch).eq("id", id);
      if (error) throw error;
      await refresh();
    },
    [refresh]
  );


  const archiveWatch = useCallback(
    async (id: string) => {
      const { error } = await supabase.from("dispatcher_watches").update({ status: "archived" }).eq("id", id);
      if (error) throw error;
      await refresh();
    },
    [refresh]
  );

  const toggleStatus = useCallback(
    async (id: string, current: DispatcherWatch["status"]) => {
      const next = current === "active" ? "paused" : "active";
      const { error } = await supabase.from("dispatcher_watches").update({ status: next }).eq("id", id);
      if (error) throw error;
      await refresh();
    },
    [refresh]
  );

  return { watches, loading, refresh, createWatch, updateWatch, archiveWatch, toggleStatus };
}
